import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';

const DEFAULT_LOYALTY_FORM = {
  points_per_kg_air: 10,
  points_per_cbm_sea: 10,
  referral_points: 200,
  streak_bonus_points: 100,
  tiers: {
    bronze: { min_points: 0, discount_percent: 0 },
    silver: { min_points: 500, discount_percent: 5 },
    gold: { min_points: 2000, discount_percent: 10 },
  },
};

export default function AdminPricing() {
  const [rates, setRates] = useState([]);
  const [form, setForm] = useState({ route: 'CN_GH', cbm_rate_usd: '', kg_rate_usd: '', exchange_rate: '' });
  const [settingsForm, setSettingsForm] = useState({ manual_mobile_money_number: '' });
  const [loyaltyForm, setLoyaltyForm] = useState(DEFAULT_LOYALTY_FORM);
  const [saving, setSaving] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingLoyalty, setSavingLoyalty] = useState(false);

  const loadRates = () => axios.get('/api/admin/pricing').then((r) => setRates(r.data)).catch(() => {});
  const loadSettings = () => axios.get('/api/admin/pricing/settings').then((r) => {
    setSettingsForm({ manual_mobile_money_number: r.data.manual_mobile_money_number || '' });
    setLoyaltyForm(r.data.loyalty_program || DEFAULT_LOYALTY_FORM);
  }).catch(() => {});

  useEffect(() => {
    loadRates();
    loadSettings();
  }, []);

  const saveRates = async () => {
    if (!form.exchange_rate) return toast.error('Exchange rate is required');

    setSaving(true);
    try {
      await axios.post('/api/admin/pricing', form);
      toast.success('Rates updated');
      loadRates();
    } catch {
      toast.error('Failed to update rates');
    } finally {
      setSaving(false);
    }
  };

  const saveManualMobileMoney = async () => {
    if (!settingsForm.manual_mobile_money_number.trim()) {
      return toast.error('Manual mobile money number is required');
    }

    setSavingSettings(true);
    try {
      await axios.put('/api/admin/pricing/settings/manual-mobile-money', settingsForm);
      toast.success('Manual payment number updated');
      loadSettings();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update manual payment number');
    } finally {
      setSavingSettings(false);
    }
  };

  const saveLoyaltyProgram = async () => {
    setSavingLoyalty(true);
    try {
      const payload = {
        ...loyaltyForm,
        points_per_kg_air: Number(loyaltyForm.points_per_kg_air),
        points_per_cbm_sea: Number(loyaltyForm.points_per_cbm_sea),
        referral_points: Number.parseInt(loyaltyForm.referral_points, 10),
        streak_bonus_points: Number.parseInt(loyaltyForm.streak_bonus_points, 10),
        tiers: {
          bronze: {
            min_points: 0,
            discount_percent: Number(loyaltyForm.tiers.bronze.discount_percent),
          },
          silver: {
            min_points: Number.parseInt(loyaltyForm.tiers.silver.min_points, 10),
            discount_percent: Number(loyaltyForm.tiers.silver.discount_percent),
          },
          gold: {
            min_points: Number.parseInt(loyaltyForm.tiers.gold.min_points, 10),
            discount_percent: Number(loyaltyForm.tiers.gold.discount_percent),
          },
        },
      };

      const response = await axios.put('/api/admin/pricing/settings/loyalty-program', payload);
      setLoyaltyForm(response.data.loyalty_program || payload);
      toast.success('Loyalty program updated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update loyalty program');
    } finally {
      setSavingLoyalty(false);
    }
  };

  const getRate = (route, field) => {
    const rate = rates.find((row) => row.route === route);
    return rate ? parseFloat(rate[field] || 0) : null;
  };

  const previewCbmGhs = form.cbm_rate_usd && form.exchange_rate
    ? (parseFloat(form.cbm_rate_usd) * parseFloat(form.exchange_rate)).toFixed(2)
    : null;

  const previewKgGhs = form.kg_rate_usd && form.exchange_rate
    ? (parseFloat(form.kg_rate_usd) * parseFloat(form.exchange_rate)).toFixed(2)
    : null;

  const routeCards = [
    { route: 'CN_GH', flag: 'CN', label: 'China to Ghana' },
    { route: 'TR_GH', flag: 'TR', label: 'Turkey to Ghana' },
  ];

  const updateTier = (tierKey, field, value) => {
    setLoyaltyForm((current) => ({
      ...current,
      tiers: {
        ...current.tiers,
        [tierKey]: {
          ...current.tiers[tierKey],
          [field]: value,
        },
      },
    }));
  };

  return (
    <div>
      <div className="page-header">
        <Link to="/admin" className="back-btn">&larr;</Link>
        <h1>Pricing</h1>
      </div>

      <div className="page-content">
        <p className="section-title">Current Active Rates</p>
        {routeCards.map((routeCard) => {
          const exchangeRate = getRate(routeCard.route, 'exchange_rate');

          return (
            <div className="card" key={routeCard.route} style={{ marginBottom: 12 }}>
              <p style={{ fontWeight: 700, marginBottom: 10 }}>{routeCard.flag} {routeCard.label}</p>

              {exchangeRate && (
                <div style={{ background: '#fef9c3', border: '1px solid #fde047', borderRadius: 8, padding: '8px 12px', marginBottom: 10 }}>
                  <p style={{ fontSize: 13, fontWeight: 700, color: '#854d0e' }}>
                    Exchange Rate: 1 USD = {exchangeRate} GHS
                  </p>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div style={{ background: '#e0f2fe', borderRadius: 8, padding: '10px 12px' }}>
                  <p style={{ fontSize: 10, color: '#0369a1', fontWeight: 700, marginBottom: 4 }}>SEA (per CBM)</p>
                  <p style={{ fontSize: 11, color: '#0369a1' }}>USD: <strong>${getRate(routeCard.route, 'cbm_rate_usd') || '—'}</strong></p>
                  <p style={{ fontSize: 13, fontWeight: 800, color: 'var(--steel-soft)', marginTop: 2 }}>
                    GHS {getRate(routeCard.route, 'cbm_rate') || '—'}
                  </p>
                </div>

                <div style={{ background: '#f0fdf4', borderRadius: 8, padding: '10px 12px' }}>
                  <p style={{ fontSize: 10, color: '#15803d', fontWeight: 700, marginBottom: 4 }}>AIR (per KG)</p>
                  <p style={{ fontSize: 11, color: '#15803d' }}>USD: <strong>${getRate(routeCard.route, 'kg_rate_usd') || '—'}</strong></p>
                  <p style={{ fontSize: 13, fontWeight: 800, color: '#16a34a', marginTop: 2 }}>
                    GHS {getRate(routeCard.route, 'kg_rate') || '—'}
                  </p>
                </div>
              </div>
            </div>
          );
        })}

        <p className="section-title" style={{ marginTop: 16 }}>Update Rates</p>
        <div className="card">
          <div className="input-group">
            <label>Route</label>
            <select className="input-field" value={form.route} onChange={(e) => setForm({ ...form, route: e.target.value })}>
              <option value="CN_GH">China to Ghana</option>
              <option value="TR_GH">Turkey to Ghana</option>
            </select>
          </div>

          <div className="input-group">
            <label style={{ color: '#d97706' }}>Exchange Rate (1 USD = ? GHS)</label>
            <input
              className="input-field"
              type="number"
              step="0.01"
              placeholder="e.g. 15.50"
              value={form.exchange_rate}
              onChange={(e) => setForm({ ...form, exchange_rate: e.target.value })}
              style={{ border: '1.5px solid #f59e0b' }}
            />
            <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
              Update this whenever the USD/GHS rate changes. All new uploads will use this rate.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div className="input-group" style={{ marginBottom: 0 }}>
              <label>Sea Rate (USD per CBM)</label>
              <input
                className="input-field"
                type="number"
                step="0.01"
                placeholder="e.g. 35.00"
                value={form.cbm_rate_usd}
                onChange={(e) => setForm({ ...form, cbm_rate_usd: e.target.value })}
              />
              {previewCbmGhs && (
                <p style={{ fontSize: 11, color: 'var(--steel-soft)', marginTop: 4, fontWeight: 600 }}>
                  = GHS {previewCbmGhs} per CBM
                </p>
              )}
            </div>

            <div className="input-group" style={{ marginBottom: 0 }}>
              <label>Air Rate (USD per KG)</label>
              <input
                className="input-field"
                type="number"
                step="0.01"
                placeholder="e.g. 5.00"
                value={form.kg_rate_usd}
                onChange={(e) => setForm({ ...form, kg_rate_usd: e.target.value })}
              />
              {previewKgGhs && (
                <p style={{ fontSize: 11, color: '#16a34a', marginTop: 4, fontWeight: 600 }}>
                  = GHS {previewKgGhs} per KG
                </p>
              )}
            </div>
          </div>

          <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '10px 12px', margin: '14px 0' }}>
            <p style={{ fontSize: 12, color: '#15803d', fontWeight: 600, marginBottom: 2 }}>Price Lock Guarantee</p>
            <p style={{ fontSize: 11, color: '#166534' }}>
              The exchange rate used for each shipment is saved at upload time.
              Future rate changes will never affect already-saved items.
            </p>
          </div>

          <button className="btn btn-primary btn-full" onClick={saveRates} disabled={saving}>
            {saving ? <span className="spinner" /> : 'Update Rates'}
          </button>
        </div>

        <p className="section-title" style={{ marginTop: 16 }}>Manual Payment Number</p>
        <div className="card">
          <div className="input-group">
            <label>Mobile Money Number</label>
            <input
              className="input-field"
              type="text"
              placeholder="e.g. 0551234567"
              value={settingsForm.manual_mobile_money_number}
              onChange={(e) => setSettingsForm({ manual_mobile_money_number: e.target.value })}
            />
            <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
              This appears on the customer payment page for manual payment.
            </p>
          </div>

          <button className="btn btn-primary btn-full" onClick={saveManualMobileMoney} disabled={savingSettings}>
            {savingSettings ? <span className="spinner" /> : 'Save Manual Payment Number'}
          </button>
        </div>

        <p className="section-title" style={{ marginTop: 16 }}>Loyalty Program</p>
        <div className="card">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div className="input-group">
              <label>Air Points per KG</label>
              <input
                className="input-field"
                type="number"
                min="0"
                step="0.01"
                value={loyaltyForm.points_per_kg_air}
                onChange={(e) => setLoyaltyForm((current) => ({ ...current, points_per_kg_air: e.target.value }))}
              />
            </div>
            <div className="input-group">
              <label>Sea Points per CBM</label>
              <input
                className="input-field"
                type="number"
                min="0"
                step="0.01"
                value={loyaltyForm.points_per_cbm_sea}
                onChange={(e) => setLoyaltyForm((current) => ({ ...current, points_per_cbm_sea: e.target.value }))}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div className="input-group">
              <label>Referral Bonus Points</label>
              <input
                className="input-field"
                type="number"
                min="0"
                step="1"
                value={loyaltyForm.referral_points}
                onChange={(e) => setLoyaltyForm((current) => ({ ...current, referral_points: e.target.value }))}
              />
            </div>
            <div className="input-group">
              <label>3-Month Streak Bonus</label>
              <input
                className="input-field"
                type="number"
                min="0"
                step="1"
                value={loyaltyForm.streak_bonus_points}
                onChange={(e) => setLoyaltyForm((current) => ({ ...current, streak_bonus_points: e.target.value }))}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gap: 12, marginTop: 8 }}>
            {[
              { key: 'bronze', label: 'Bronze', editableMin: false },
              { key: 'silver', label: 'Silver', editableMin: true },
              { key: 'gold', label: 'Gold', editableMin: true },
            ].map((tier) => (
              <div key={tier.key} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 12 }}>
                <p style={{ fontWeight: 700, marginBottom: 10 }}>{tier.label} Tier</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div className="input-group" style={{ marginBottom: 0 }}>
                    <label>Min Points</label>
                    <input
                      className="input-field"
                      type="number"
                      min="0"
                      step="1"
                      disabled={!tier.editableMin}
                      value={loyaltyForm.tiers[tier.key].min_points}
                      onChange={(e) => updateTier(tier.key, 'min_points', e.target.value)}
                    />
                  </div>
                  <div className="input-group" style={{ marginBottom: 0 }}>
                    <label>Shipping Discount %</label>
                    <input
                      className="input-field"
                      type="number"
                      min="0"
                      step="0.01"
                      value={loyaltyForm.tiers[tier.key].discount_percent}
                      onChange={(e) => updateTier(tier.key, 'discount_percent', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 12 }}>
            Bronze always starts at 0 points. Silver and Gold thresholds determine when customers move up.
          </p>

          <button className="btn btn-primary btn-full" onClick={saveLoyaltyProgram} disabled={savingLoyalty} style={{ marginTop: 16 }}>
            {savingLoyalty ? <span className="spinner" /> : 'Save Loyalty Program'}
          </button>
        </div>
      </div>
    </div>
  );
}
